import { useEffect, useState } from "react";
import { supabase } from "./supabase";

// O que as telas do aluno leem, num lugar só.
//
// Regra que vale para todo este arquivo: o front NÃO decide quem vê o quê. Toda
// consulta aqui passa pela RLS (teste 21 exercita isso logando como aluno), e as
// views já trazem a regra pronta — `vw_meus_cursos` agrega os produtos que são o
// mesmo curso e resolve a vigência do conjunto, `vw_minha_vitrine` diz que cards
// cada curso mostra. O que este módulo faz é casar e ordenar para apresentação.
// Se uma regra de acesso aparecer neste arquivo, ela está no lugar errado.

export type Matricula = {
  produto_id: string;
  produto_slug?: string;
  turma: string | null;
  inicio: string;
  previsao_termino: string | null;
  status: "ativa" | "concluida" | "cancelada";
};

export type Contrato = {
  produto_id: string;
  status: "pendente" | "assinado";
  assinado_em: string | null;
};

export type CardVitrine = {
  card_slug: string;
  card_nome: string;
  card_tipo: "porta" | "ferramenta" | "modulo" | "curso_bonus";
  /** 'curso' aparece dentro do curso; 'conta' aparece uma vez na tela inicial. */
  card_escopo: "curso" | "conta";
  descricao: string | null;
  url: string | null;
  /**
   * COMO este card abre (000062; `interno` na 000065).
   *
   * `url` = endereço fixo, link comum. `memberkit`, `sso-cfc` e `sso-sentinela`
   * (000387) precisam de credencial gerada na hora e passam pela ponte `/abrir` →
   * Edge Function `abrir-card`. `interno` = a ferramenta é DO HUB, e a rota sai do slug
   * (`/ferramenta/<slug>`) — nada a buscar no servidor. `em-breve` = destino
   * ainda não existe, e é também o que a view devolve quando o curso está
   * trancado — a trava não depende de o front lembrar de olhar outro campo.
   */
  destino: "em-breve" | "url" | "memberkit" | "sso-cfc" | "sso-sentinela" | "interno";
  universal: boolean;
  ordem: number;
  por_produtos: string[];
  por_cursos: string[];
  /** Todos os cursos que sustentam este card estão trancados por contrato (000044). */
  trancado: boolean;
};

/**
 * Um curso do aluno — não um produto do catálogo.
 *
 * A distinção é de Victor, em 12/08: "Escola PRO e Vitalícia devem ver um só, é
 * o mesmo curso. Só muda a forma como é vendido." Quem faz o agrupamento e
 * resolve a vigência do conjunto é `vw_meus_cursos` (000038); aqui o curso já
 * chega inteiro.
 */
export type CursoDoAluno = {
  slug: string;
  nome: string;
  fim_efetivo: string | null;
  vitalicio: boolean;
  faculdade: boolean;
  /** Os SKUs que sustentam este curso. Dois na escola de quem tem Pro e vitalícia. */
  produtos: string[];
  fontes: number;
  matricula: Matricula | null;
  contrato: Contrato | null;
  /** Há contrato aguardando assinatura em algum produto deste curso. */
  contratoPendente: boolean;
  /**
   * O curso está trancado até o contrato ser assinado (000044).
   *
   * Quem decide é o banco: `exige_contrato` no produto E contrato pendente. O
   * front não recalcula — e nem poderia, porque a view também anula a `url` dos
   * cards, então a trava vale mesmo para quem abrir o DevTools.
   */
  trancado: boolean;
};

/** O que o selo de vigência precisa saber. Curso ou produto servem. */
export type Vigencia = { fim_efetivo: string | null; vitalicio: boolean };

/** Uma seção da tela de curso, com os slugs dos cards que moram nela. */
export type SecaoDoCurso = {
  titulo: string;
  ordem: number;
  cards: string[];
};

export type Hub = {
  nome: string | null;
  /** Foto do aluno, ou nulo — que é o estado dos 58 mil perfis da carga. */
  avatarUrl: string | null;
  /** Cursos livres: Escola Contábil e Aprovação Contábil. */
  cursos: CursoDoAluno[];
  /** A Faculdade: MBA, Perícia e as 6 pós — os que têm matrícula e secretaria. */
  faculdade: CursoDoAluno[];
  /** Cards de escopo 'curso': aparecem dentro do curso a que pertencem. */
  cardsDeCurso: CardVitrine[];
  /** Cards de escopo 'conta': aparecem uma vez na tela inicial. */
  cardsDeConta: CardVitrine[];
  /**
   * Seções por curso, quando o curso tem (000041). Curso ausente deste mapa usa
   * o layout padrão — portas em cima, ferramentas embaixo —, que é o certo para
   * quem tem quatro ou cinco cards.
   */
  secoesPorCurso: Map<string, SecaoDoCurso[]>;
  /**
   * Endereço de assinatura do contrato, por curso.
   *
   * Vem RESOLVIDO de `vw_meus_cursos.contrato_url` (000051): a variante da
   * compra do aluno vence — Recorrência e Assinatura Anual assinam o COM
   * renovação, CFC assina o contrato próprio — e sem variante vale o contrato
   * padrão do produto. Quem decide se o link serve é o componente que o usa —
   * mandar o aluno a um endereço que não assina é pior que dizer que o link
   * chega por e-mail.
   */
  contratoUrlPorCurso: Map<string, string>;
  /**
   * A senha ainda não foi sincronizada com o Memberkit (`mk_senha_em` nulo).
   *
   * Vem daqui, e não de uma consulta própria, porque `useHub` já lê `profiles`:
   * quem confirma o e-mail pelo link cai logado sem passar pelo formulário de
   * entrada, e é a tela inicial que tem de pedir a senha uma vez. Sem isto o
   * aluno fica com senha diferente nas aulas e nada na tela oferece a saída —
   * foi exatamente o que a reescrita desta Fase quebrou ao apagar a vitrine
   * antiga, que era o único lugar que montava o banner.
   */
  senhaPendenteNasAulas: boolean;
};

export type EstadoHub =
  | { fase: "carregando" }
  | { fase: "erro" }
  // Conta confirmada mas sem perfil vinculado: a RLS devolve zero perfis. Não é
  // erro de sistema — é o caso "sem_perfil" da vw_vinculos_pendentes, e a tela
  // orienta a pessoa em vez de mostrar uma prateleira vazia muda.
  | { fase: "sem-vinculo" }
  | { fase: "pronto"; hub: Hub };

type LinhaCurso = {
  curso_slug: string;
  curso_nome: string;
  fim_efetivo: string | null;
  vitalicio: boolean;
  faculdade: boolean;
  produtos: string[];
  fontes: number;
  contrato_pendente: boolean;
  /** Fato sobre o ALUNO: este curso está trancado por contrato pendente? */
  trancado: boolean;
  /**
   * Fato sobre o PEDIDO: eu, que estou perguntando, estou trancado? (000063)
   *
   * As duas colunas existem porque são duas perguntas, e o front sempre faz a
   * segunda: ele é a tela de quem está logado. Elas só divergem para operador
   * (papel `equipe` ou `admin`), que por decisão de Victor em 19/08 não é
   * bloqueado por contrato — ele precisa abrir as ferramentas que confere.
   * Quem mostra a primeira é o dossiê do painel, sobre OUTRA pessoa.
   */
  trancado_para_mim: boolean;
  contrato_url: string | null;
};

export function useHub(): EstadoHub {
  const [estado, setEstado] = useState<EstadoHub>({ fase: "carregando" });

  useEffect(() => {
    let ativo = true;
    (async () => {
      const [perfis, cursos, produtos, matriculas, contratos, vitrine, secoes] = await Promise.all([
        supabase.from("profiles").select("nome, mk_senha_em, avatar_url").limit(1),
        supabase.from("vw_meus_cursos").select("*"),
        supabase.from("produtos").select("id, slug, curso_slug"),
        supabase.from("matriculas").select("produto_id, turma, inicio, previsao_termino, status"),
        supabase.from("contratos").select("produto_id, status, assinado_em"),
        supabase.from("vw_minha_vitrine").select("*").order("ordem").order("card_nome"),
        supabase.from("vw_vitrine_secoes").select("*").order("secao_ordem").order("card_ordem"),
      ]);

      if (!ativo) return;

      const falhou = [perfis, cursos, produtos, matriculas, contratos, vitrine, secoes].some(
        (r) => r.error,
      );
      if (falhou) {
        setEstado({ fase: "erro" });
        return;
      }
      if ((perfis.data ?? []).length === 0) {
        setEstado({ fase: "sem-vinculo" });
        return;
      }

      // produto_id → curso, para achar a matrícula e o contrato de um curso cujos
      // SKUs a view já resumiu em `produtos: string[]`.
      const linhasProduto = produtos.data as {
        id: string;
        slug: string;
        curso_slug: string;
      }[];
      const cursoDoProdutoId = new Map(linhasProduto.map((p) => [p.id, p.curso_slug]));

      const matriculasPorCurso = new Map<string, Matricula[]>();
      for (const m of matriculas.data as Matricula[]) {
        const curso = cursoDoProdutoId.get(m.produto_id);
        if (!curso) continue;
        (matriculasPorCurso.get(curso) ?? matriculasPorCurso.set(curso, []).get(curso)!).push(m);
      }

      const contratosPorCurso = new Map<string, Contrato[]>();
      for (const c of contratos.data as Contrato[]) {
        const curso = cursoDoProdutoId.get(c.produto_id);
        if (!curso) continue;
        (contratosPorCurso.get(curso) ?? contratosPorCurso.set(curso, []).get(curso)!).push(c);
      }

      const meus: CursoDoAluno[] = (cursos.data as LinhaCurso[]).map((l) => ({
        slug: l.curso_slug,
        nome: l.curso_nome,
        fim_efetivo: l.fim_efetivo,
        vitalicio: l.vitalicio,
        faculdade: l.faculdade,
        produtos: l.produtos,
        fontes: l.fontes,
        // Com mais de um SKU no mesmo curso pode haver mais de uma matrícula ou
        // contrato. A ativa e o assinado ganham: são o estado que o aluno
        // reconhece como o seu, e mostrar "cancelada" para quem rematriculou
        // seria dar a notícia errada.
        matricula: escolher(matriculasPorCurso.get(l.curso_slug), (m) => m.status === "ativa"),
        // Aqui o PENDENTE ganha, ao contrário da matrícula: quando o curso está
        // trancado, é o contrato a assinar que a tela precisa mostrar, não o que
        // já foi assinado.
        contrato: escolher(
          contratosPorCurso.get(l.curso_slug),
          l.trancado ? (c) => c.status === "pendente" : (c) => c.status === "assinado",
        ),
        contratoPendente: l.contrato_pendente,
        // `trancado_para_mim`, não `trancado`: o front é sempre a tela de quem
        // está logado, e é essa a coluna que a `vw_minha_vitrine` já usa para
        // anular url e destino (000063). Ler a outra faria a página avisar
        // "assine o contrato" com os cards abertos do lado — e faria o operador
        // ver tudo fechado numa tela que o banco liberou.
        trancado: l.trancado_para_mim,
      }));

      // As seções vêm planas (uma linha por card) e já ordenadas pela view;
      // agrupar aqui preserva essa ordem, que é a ditada por Victor — "IR PRO"
      // antes de "IR Proposta", e não a alfabética.
      const secoesPorCurso = new Map<string, SecaoDoCurso[]>();
      for (const l of (secoes.data ?? []) as {
        curso_slug: string;
        secao: string;
        secao_ordem: number;
        card_slug: string;
      }[]) {
        const lista = secoesPorCurso.get(l.curso_slug) ?? [];
        if (!secoesPorCurso.has(l.curso_slug)) secoesPorCurso.set(l.curso_slug, lista);
        const existente = lista.find((s) => s.titulo === l.secao);
        if (existente) existente.cards.push(l.card_slug);
        else lista.push({ titulo: l.secao, ordem: l.secao_ordem, cards: [l.card_slug] });
      }

      // O link de assinatura vem RESOLVIDO do banco (vw_meus_cursos, 000051):
      // a variante da compra do aluno vence o contrato padrão do produto —
      // Recorrência e Assinatura Anual assinam o COM renovação, CFC assina o
      // contrato próprio. Escolher o link aqui em JavaScript seria criar uma
      // segunda definição de "qual contrato o aluno assina".
      const contratoUrlPorCurso = new Map<string, string>();
      for (const l of cursos.data as LinhaCurso[]) {
        if (l.contrato_url) contratoUrlPorCurso.set(l.curso_slug, l.contrato_url);
      }

      const porNome = (a: CursoDoAluno, b: CursoDoAluno) => a.nome.localeCompare(b.nome, "pt-BR");
      const cards = (vitrine.data ?? []) as CardVitrine[];
      const perfil = (
        perfis.data as {
          nome: string | null;
          mk_senha_em: string | null;
          avatar_url: string | null;
        }[]
      )[0];

      setEstado({
        fase: "pronto",
        hub: {
          nome: perfil.nome,
          avatarUrl: perfil.avatar_url,
          cursos: meus.filter((c) => !c.faculdade).sort(porNome),
          faculdade: meus.filter((c) => c.faculdade).sort(porNome),
          cardsDeCurso: cards.filter((c) => c.card_escopo === "curso"),
          cardsDeConta: cards.filter((c) => c.card_escopo === "conta"),
          secoesPorCurso,
          contratoUrlPorCurso,
          senhaPendenteNasAulas: perfil.mk_senha_em === null,
        },
      });
    })();
    return () => {
      ativo = false;
    };
  }, []);

  return estado;
}

function escolher<T>(lista: T[] | undefined, preferido: (item: T) => boolean): T | null {
  if (!lista || lista.length === 0) return null;
  return lista.find(preferido) ?? lista[0];
}

/**
 * Os cards de UM curso: os universais mais os que aquele curso libera.
 *
 * A `vw_minha_vitrine` entrega um card por linha com a lista de cursos que o
 * sustentam — é o que permite não repetir "Calendário Tributário" três vezes
 * para quem tem Escola, Pós RT e Pós Rotinas. Aqui a lista é filtrada de volta
 * para um curso só, porque **ferramenta aparece dentro do seu curso**, nunca
 * solta numa tela geral.
 *
 * Filtra por `por_cursos`, e não por `por_produtos`: com as duas escolas viradas
 * um curso só, filtrar por SKU esconderia a ferramenta de quem comprou o outro.
 *
 * E o universal também vem por `por_cursos`. Até 30/10 ele entrava em TODO curso
 * (`c.universal || …`), o que era redundante enquanto todo produto recebia os
 * universais. Desde a 000375 o curso cujas aulas moram no hub não os recebe, e a
 * vitrine já tira o curso dele de `por_cursos`: com o `||`, o aluno que tem a Escola
 * e o Fundamentos da Reforma via a porta "Aulas" do Memberkit no curso que não tem
 * escola — e o clique morria na ponte.
 */
export function cardsDoCurso(cards: CardVitrine[], slugCurso: string): CardVitrine[] {
  return cards.filter((c) => c.por_cursos.includes(slugCurso));
}

/**
 * O que a tela precisa saber para desenhar o card: abre, para onde, e se o
 * destino é DAQUI.
 *
 * `interno` decide aba e componente, não só estilo: destino de fora abre em
 * `<a target="_blank">` (é outro sistema, e o aviso ao leitor de tela existe por
 * isso), e destino do hub abre em `<Link>` na mesma aba — nova aba para uma rota
 * do próprio app dá duas cópias do hub abertas, e `rel="noreferrer"` num link
 * interno força recarga completa do bundle. Quem escolhe é `LinkDoCard`, uma vez.
 */
export type AberturaDoCard =
  | { abre: false }
  | {
      abre: true;
      href: string;
      interno: boolean;
      /**
       * O card `url` abre por `<a>`, sem passar por servidor nenhum — e sem
       * isto, o clique não deixaria rastro (000303, F10). Os que passam pela
       * ponte `/abrir` NÃO levam este campo: a `abrir-card` já registra, e
       * registrar aqui também contaria duas vezes. O interno registra na própria
       * página (`Ferramenta.tsx`), onde a abertura de fato acontece.
       */
      registrar?: { card: string; curso: string | null };
    };

/**
 * O card abre, e por qual endereço? Uma definição para as seis telas de card.
 *
 * Antes cada componente decidia com `Boolean(card.url) && !travado`, repetido em
 * seis lugares — e com a Fase 4 a regra deixou de caber num booleano: destino
 * gerado na hora não tem url para testar. Uma cópia esquecida agora não é card
 * feio, é porta que não abre.
 *
 * `cursoSlug` é o curso da TELA em que o card está sendo desenhado, e ele importa
 * duas vezes: é quem resolve a escola do Memberkit, e é quem faz valer a regra de
 * 12/08 — card compartilhado com curso em dia continua aberto na tela daquele
 * curso, mas na tela do curso trancado nada abre, para a página não contradizer
 * o próprio aviso.
 */
/**
 * Dois cards internos NÃO moram em `/ferramenta/<slug>`: eles são ABAS da
 * Faculdade, e a aba é sub-rota (`/faculdade/:aba`).
 *
 * `Faculdade.tsx` já sabia disso pela metade — ele exclui os dois da visão geral
 * ("Secretaria e Minhas Notas têm aba própria; não se repetem"). O que faltava era
 * a outra metade: na página do CURSO os dois apareciam, e ficaram em `em-breve`
 * de 31/08 a 09/09 com o produto pronto do lado. 38.799 alunos das oito pós liam
 * "em breve" em duas portas cujo conteúdo funcionava em `/faculdade`.
 *
 * Virar só o `destino` no banco teria piorado: sem tela em `FERRAMENTAS`, a rota
 * derivada cai no aviso "esta ferramenta abre por outro caminho, volte à página do
 * curso e clique no card dela" — apontando de volta para o card que a pessoa
 * acabou de clicar.
 *
 * O mapa fica AQUI, e não em `aberturaDoCard`, porque esta função é A definição da
 * rota (o comentário abaixo) — quem deriva por fora recomeça a divergência.
 */
const ABA_DA_FACULDADE: Record<string, string> = {
  "secretaria-do-aluno": "secretaria",
  "minhas-notas": "notas",
};

/**
 * A rota de uma ferramenta interna, derivada do slug — 000065. É A definição:
 * `aberturaDoCard` e a ponte `/abrir` usam esta função, e mais ninguém deriva.
 */
export const rotaDaFerramenta = (slug: string) => {
  const aba = ABA_DA_FACULDADE[slug];
  if (aba) return `/faculdade/${aba}`;
  return `/ferramenta/${encodeURIComponent(slug)}`;
};

export function aberturaDoCard(
  card: CardVitrine,
  cursoSlug: string | null,
  travado?: boolean,
): AberturaDoCard {
  // `card.trancado` (todos os cursos do card trancados) e `travado` (a tela é de
  // um curso trancado) são coisas diferentes, e as duas fecham a porta.
  if (travado || card.trancado) return { abre: false };

  if (card.destino === "em-breve") return { abre: false };
  // Endereço fixo é link comum. A url pode vir nula mesmo com destino 'url' se a
  // view anular por outro motivo — então o teste continua aqui.
  if (card.destino === "url") {
    return card.url
      ? { abre: true, href: card.url, interno: false, registrar: { card: card.card_slug, curso: cursoSlug } }
      : { abre: false };
  }

  // Ferramenta do próprio hub (000065): a rota é DERIVADA do slug, e não vem do
  // banco. Derivar é o que impede o terceiro estado — card com destino interno e
  // url de outra coisa — e é o que faz MEI PRO e IR PRO entrarem depois sem tocar
  // nesta função. Não passa pela ponte `/abrir`: não há credencial a gerar, e
  // quem autoriza a tela é a RLS das tabelas dela.
  //
  // O curso da tela vai na QUERY, e para a IA ele não é enfeite: é qual corpus
  // responde (decisão de Victor de 19/08 — o Contabiliza.AI é UM card em DOIS
  // cursos, com material próprio em cada um). Na query e não no caminho porque o
  // `*` de `/ferramenta/:slug/*` já é o despacho interno do MEI PRO. Para as
  // ferramentas que ignoram o curso, o parâmetro é inócuo.
  if (card.destino === "interno") {
    const query = card.card_escopo === "curso" && cursoSlug
      ? `?curso=${encodeURIComponent(cursoSlug)}`
      : "";
    return { abre: true, href: rotaDaFerramenta(card.card_slug) + query, interno: true };
  }

  // Destino gerado na hora: a ponte `/abrir` chama a Edge Function e redireciona.
  // O sentinela 'conta' é para o card de escopo 'conta' (Contrate um Contador),
  // que não pertence a curso — nenhum curso do catálogo se chama assim.
  const escopo = card.card_escopo === "conta" ? "conta" : cursoSlug;
  if (!escopo) return { abre: false };
  return {
    abre: true,
    href: `/abrir/${encodeURIComponent(escopo)}/${encodeURIComponent(card.card_slug)}`,
    interno: false,
  };
}

// ---------------------------------------------------------------------------
// Formatação — pt-BR em tudo, e datas sem fuso surpresa
// ---------------------------------------------------------------------------

/**
 * `fim_efetivo` é timestamptz e `inicio`/`previsao_termino` são date. Um date
 * chega como "2027-03-01" e `new Date()` sobre isso é interpretado como UTC —
 * em fuso negativo o dia volta um. Por isso a data pura é quebrada à mão.
 */
export function formatarData(valor: string | null): string {
  if (!valor) return "—";
  const soData = /^\d{4}-\d{2}-\d{2}$/.exec(valor);
  if (soData) {
    const [ano, mes, dia] = valor.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  return new Date(valor).toLocaleDateString("pt-BR");
}

export function formatarMesAno(valor: string | null): string {
  if (!valor) return "—";
  const partes = /^(\d{4})-(\d{2})/.exec(valor);
  if (!partes) return formatarData(valor);
  const meses = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${meses[Number(partes[2]) - 1]}/${partes[1]}`;
}

/** Dias que faltam para a vigência acabar. Negativo não acontece: a view só
 *  devolve acesso não expirado. */
export function diasAte(fim: string): number {
  return Math.ceil((new Date(fim).getTime() - Date.now()) / 86_400_000);
}

/**
 * A vigência que o aluno precisa ver primeiro: a que acaba antes.
 *
 * Devolve o curso junto com a data de propósito. Mostrar só o número faz o
 * aluno com um curso vitalício e outro vencendo ler a data como se o hub
 * inteiro fosse expirar — o rótulo tem de nomear o curso.
 */
export function vigenciaMaisProxima(meus: CursoDoAluno[]): CursoDoAluno | null {
  const datados = meus.filter((c) => !c.vitalicio && c.fim_efetivo);
  if (datados.length === 0) return null;
  return datados.reduce((a, b) => (a.fim_efetivo! <= b.fim_efetivo! ? a : b));
}

/**
 * O link de assinatura serve, ou é o placeholder antigo?
 *
 * A distinção já mordeu uma vez: a primeira heurística era "contém /verificar/ =
 * placeholder", e os links REAIS da ZapSign (000049) vieram como
 * `/verificar/doc/<uuid>` — o formato legítimo também usa /verificar/, e o botão
 * de assinar sumiu exatamente de quem ele servia. O placeholder se reconhece
 * pelo caminho sem `/doc/`; hoje só a escola-contabil continua assim.
 */
export function linkDeAssinaturaServe(url: string | null | undefined): boolean {
  return Boolean(url && (url.includes("/verificar/doc/") || !url.includes("/verificar/")));
}

export function saudacao(): string {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

export function primeiroNome(nome: string | null): string | null {
  if (!nome) return null;
  const p = nome.trim().split(/\s+/)[0];
  return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : null;
}

export function iniciais(nome: string | null): string {
  if (!nome) return "•";
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "•";
  const primeira = partes[0].charAt(0);
  const ultima = partes.length > 1 ? partes[partes.length - 1].charAt(0) : "";
  return (primeira + ultima).toUpperCase();
}
